import React, { useCallback, useEffect, useRef, useState } from "react";
import { Header } from "../components/Header";
import { SiteFooter } from "../components/SiteFooter";
import { apiRequest } from "../lib/api";
import { createFeedbackSubmissionId, getFeedbackSessionId } from "../lib/feedbackSession";
import { parseYouTubeVideoId } from "../lib/youtube";
import { FeedbackThread } from "../components/FeedbackThread";
import { getFeedbackWatch, loadFeedbackUpdates, watchFeedback } from "../lib/feedbackNotifications";

export function FeedbackPage({ theme, onToggleTheme, onNavigate }) {
  const [videoId, setVideoId] = useState(() => parseYouTubeVideoId(new URLSearchParams(window.location.search).get("video")));
  const [posts, setPosts] = useState([]);
  const [updatedPosts, setUpdatedPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedError, setFeedError] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const submissionId = useRef(null);
  const submittingRef = useRef(false);

  const loadPosts = useCallback(async (before = null) => {
    setLoading(true);
    setFeedError("");
    try {
      const result = await apiRequest(`/api/theofast/feedback${before ? `?before=${encodeURIComponent(before)}` : ""}`, { cache: "no-store" });
      setPosts(current => before ? [...current, ...(result.posts || [])].filter((post, index, all) => all.findIndex(item => item.id === post.id) === index) : result.posts || []);
      setCursor(result.nextCursor || null);
    } catch (error) { setFeedError(error.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);
  useEffect(() => {
    let active = true;
    const refresh = () => loadFeedbackUpdates().then(items => { if (active) setUpdatedPosts(items); }).catch(() => {});
    refresh();
    window.addEventListener("focus", refresh);
    const timer = window.setInterval(refresh, 60000);
    return () => { active = false; window.removeEventListener("focus", refresh); window.clearInterval(timer); };
  }, []);

  useEffect(() => {
    if (/^#post-[a-f0-9]{24}$/i.test(window.location.hash)) document.getElementById(window.location.hash.slice(1))?.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [posts]);

  const submit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError("");
    setSubmitMessage("");
    if (!submissionId.current) submissionId.current = createFeedbackSubmissionId();
    try {
      const result = await apiRequest("/api/theofast/feedback", { method: "POST", body: JSON.stringify({ title, message, sessionId: getFeedbackSessionId(), submissionId: submissionId.current, ...(videoId ? { videoId } : {}) }) });
      watchFeedback(result.post);
      setPosts(current => [result.post, ...current.filter(post => post.id !== result.post.id)]);
      setTitle("");
      setMessage("");
      submissionId.current = null;
      setSubmitMessage("Posted. Thanks for making TheoFast better.");
    } catch (error) { setSubmitError(error.message); }
    finally { submittingRef.current = false; setSubmitting(false); }
  };

  return (
    <div className="app-shell">
      <Header theme={theme} onToggleTheme={onToggleTheme} onNavigate={onNavigate} />
      <main className="section-wrap feedback-page">
        <button className="feedback-back" type="button" onClick={() => onNavigate(videoId ? `/watch/${videoId}` : "/")}>← Back to watching</button>
        <div className="feedback-intro"><span className="eyebrow">The feedback corner</span><h1>What could be better?</h1><p>Found an issue? Have an idea? Leave it here. No account or email needed.</p><p>Check back in this browser for replies. A red badge will let you know.</p></div>
        <form className="feedback-form" onSubmit={submit}>
          <label htmlFor="feedback-title">Title</label>
          <input id="feedback-title" value={title} onChange={event => setTitle(event.target.value)} minLength={3} maxLength={100} required disabled={submitting} placeholder="A small idea. A strange bug." />
          <label htmlFor="feedback-message">Details</label>
          <textarea id="feedback-message" value={message} onChange={event => setMessage(event.target.value)} minLength={10} maxLength={2000} required disabled={submitting} rows={4} placeholder="What happened, or what would you like to see?" aria-describedby="feedback-public-note" />
          {videoId && <p className="feedback-video-note">This video is attached to your post. <a href={`https://youtu.be/${videoId}`} target="_blank" rel="noreferrer">View video ↗</a> <button type="button" className="feedback-remove-video" disabled={submitting} onClick={() => { setVideoId(null); const url = new URL(window.location.href); url.searchParams.delete("video"); window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash); }}>Remove</button></p>}
          <div className="feedback-form-bottom"><p id="feedback-public-note">Posts are public. Please leave out personal details.</p><button className="primary-button" type="submit" disabled={submitting}>{submitting ? "Posting…" : "Post feedback"}</button></div>
          {submitError && <p className="feedback-error" role="alert">{submitError}</p>}
          {submitMessage && <p className="feedback-success" role="status">{submitMessage}</p>}
        </form>
        <section className="feedback-feed" aria-labelledby="feedback-feed-title">
          <div className="feedback-feed-heading"><h2 id="feedback-feed-title">Community feedback</h2><button type="button" className="feedback-back" onClick={() => loadPosts()} disabled={loading}>Refresh</button></div>
          {[...updatedPosts, ...posts.filter(post => !updatedPosts.some(updated => updated.id === post.id))].map(post => <article className="feedback-post" id={`post-${post.id}`} key={post.id}>
            {updatedPosts.some(item => item.id === post.id) && <span className="feedback-new-reply">New admin reply</span>}
            <div className="feedback-post-meta">{post.status === "fixed" && <span className="feedback-fixed">Fixed</span>}<span>{post.authorLabel}</span><time dateTime={post.createdAt} title={new Date(post.createdAt).toLocaleString(undefined, { timeZoneName: "short" })}>{new Date(post.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · {new Date(post.createdAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</time></div>
            <h3>{post.title}</h3><p>{post.message}</p>
            {post.videoId && <a className="feedback-post-video" href={`/watch/${post.videoId}`} onClick={event => { if (!event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) { event.preventDefault(); onNavigate(`/watch/${post.videoId}`); } }}>Related video ↗</a>}
            <FeedbackThread post={post} tracked={Object.hasOwn(getFeedbackWatch(), post.id) || updatedPosts.some(item => item.id === post.id)} onUpdate={updated => { setPosts(current => [updated, ...current.filter(item => item.id !== updated.id)]); setUpdatedPosts(current => current.filter(item => item.id !== updated.id)); }} />
          </article>)}
          {loading && <p className="feedback-feed-note" role="status">Loading feedback…</p>}
          {feedError && <div className="feedback-error" role="alert"><p>{feedError}</p><button className="feedback-back" type="button" onClick={() => loadPosts(cursor)}>Try again</button></div>}
          {!loading && !feedError && posts.length === 0 && <p className="feedback-feed-note">Quiet in here. Your idea can be the first.</p>}
          {cursor && !loading && <button className="feedback-more" type="button" onClick={() => loadPosts(cursor)}>Older posts</button>}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
